const CACHE_KEY = "timesheet-data-cache-v1";
const DEFAULT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_CACHE_ENTRIES = 24;

const getCurrentUserKey = () => {
  if (typeof window === "undefined") return "";

  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return String(user?._id || user?.id || user?.username || "").trim();
  } catch (error) {
    return "";
  }
};

const normalizeKeyParts = (value) => {
  if (Array.isArray(value)) return value.map(normalizeKeyParts);
  if (!value || typeof value !== "object") return value ?? "";

  return Object.keys(value)
    .sort()
    .reduce((result, key) => {
      result[key] = normalizeKeyParts(value[key]);
      return result;
    }, {});
};

const getEntryKey = (scope, keyParts) => {
  const userKey = getCurrentUserKey();
  if (!userKey) return "";
  return `${userKey}:${scope}:${JSON.stringify(normalizeKeyParts(keyParts))}`;
};

const readStore = () => {
  if (typeof window === "undefined") return {};

  try {
    const parsed = JSON.parse(localStorage.getItem(CACHE_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    localStorage.removeItem(CACHE_KEY);
    return {};
  }
};

const writeStore = (store) => {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(store));
  } catch (error) {
    // A cache write must never prevent the live API data from being shown.
  }
};

export const readTimesheetCache = (
  scope,
  keyParts = {},
  maxAgeMs = DEFAULT_MAX_AGE_MS,
) => {
  const entryKey = getEntryKey(scope, keyParts);
  if (!entryKey) return null;

  const store = readStore();
  const entry = store[entryKey];
  const cachedAt = Number(entry?.cachedAt);
  const cacheIsUsable =
    Number.isFinite(cachedAt) &&
    cachedAt <= Date.now() &&
    Date.now() - cachedAt <= maxAgeMs &&
    entry?.data != null;

  if (cacheIsUsable) return entry.data;

  if (entry) {
    delete store[entryKey];
    writeStore(store);
  }
  return null;
};

export const writeTimesheetCache = (scope, keyParts = {}, data) => {
  const entryKey = getEntryKey(scope, keyParts);
  if (!entryKey || data == null) return;

  const store = readStore();
  store[entryKey] = { data, cachedAt: Date.now() };

  const prunedStore = Object.fromEntries(
    Object.entries(store)
      .sort(
        ([, first], [, second]) =>
          Number(second?.cachedAt || 0) - Number(first?.cachedAt || 0),
      )
      .slice(0, MAX_CACHE_ENTRIES),
  );
  writeStore(prunedStore);
};

export const clearTimesheetCache = () => {
  if (typeof window !== "undefined") {
    localStorage.removeItem(CACHE_KEY);
  }
};
